import { PropsWithChildren } from "react"
import { tss } from "tss-react/mui"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { Accordion, AccordionDetails, AccordionSummary, Typography, Stack, Box } from "@mui/material"

type StyleProps = Readonly<{
    size?: "small" | "medium"
}>

const useStyles = tss.withParams<StyleProps>().create(({ theme, size }) => ({
    accordion: {
        padding: size === "medium" ? "1rem 0" : ".5rem 0",
        textAlign: "center",
        borderRadius: "0.75rem",
        boxShadow: "none",
        flex: "none",
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,

        "&:first-of-type, &:last-of-type": {
            borderRadius: "0.75rem",
        },
        "&.Mui-expanded": {
            margin: 0,
        },
        "&:before": {
            display: "none",
        },
    },
    accordionSummary: {
        minHeight: "auto",
        "&.Mui-expanded": {
            minHeight: "auto",
        },
        "& .MuiAccordionSummary-content": {
            margin: 0,
            "&.Mui-expanded": {
                margin: 0,
            },
        },
    },
    titleContainer: {
        gap: ".75rem",
        display: "grid",
        alignItems: "center",
        gridTemplateColumns: "2.5rem 1fr 1.5rem",
    },
    subTitleBox: {
        gridColumn: "2",
    },
    accordionDetails: {
        textAlign: "left",
        padding: "1rem 1rem 0",
    },
    title: {
        gridColumn: "2",
        textAlign: "left",
        color: theme.palette.text.primary,
    },
    rotateIcon: {
        transform: "rotate(180deg)",
    },
    expandMoreIcon: {
        transition: "transform 0.3s ease",
        color: theme.palette.tailwind.slate[500],
    },
    contentContainer: {
        display: "grid",
        gap: "1rem",
        textAlign: "left",
    },
}))

export type AccordionSectionHandle = {
    expand: () => void
    collapse: () => void
}

type AccordionSectionProps = Readonly<
    PropsWithChildren<{
        title: string
        icon?: JSX.Element
        rightTitleDetails?: JSX.Element
        updateAccordionUi?: string
        updateAccordionContentUi?: string
        handleExpanded?: () => void
        size?: "small" | "medium"
        accordionKey: string
        expandedAccordionKey: string | null
        setExpandedAccordionKey: (key: string) => void
    }>
>

export function AccordionSection({
    title,
    icon,
    rightTitleDetails,
    updateAccordionUi = "",
    updateAccordionContentUi = "",
    children,
    handleExpanded,
    size = "medium",
    accordionKey,
    expandedAccordionKey,
    setExpandedAccordionKey,
}: AccordionSectionProps) {
    const { classes, cx } = useStyles({ size })

    const isExpanded = expandedAccordionKey === accordionKey

    const handleChange = () => {
        if (handleExpanded) {
            handleExpanded()
        } else setExpandedAccordionKey(isExpanded ? "" : accordionKey)
    }

    return (
        <Accordion expanded={isExpanded} onChange={handleChange} className={cx(classes.accordion, updateAccordionUi)}>
            <AccordionSummary className={classes.accordionSummary}>
                <Stack gap=".625rem" width="100%">
                    <Box className={classes.titleContainer}>
                        {icon}
                        <Stack flexDirection="row" alignItems="center" gap=".75rem">
                            <Typography variant="subtitle1" className={classes.title}>
                                {title}
                            </Typography>
                            {!isExpanded && rightTitleDetails && (
                                <Box className={classes.subTitleBox}>{rightTitleDetails}</Box>
                            )}
                        </Stack>
                        <ExpandMoreIcon className={cx({ [classes.rotateIcon]: isExpanded }, classes.expandMoreIcon)} />
                    </Box>
                </Stack>
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
                <Box className={cx(classes.contentContainer, updateAccordionContentUi)}>{children}</Box>
            </AccordionDetails>
        </Accordion>
    )
}
